import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { Modal } from "components/designV2/modal/modal";
import { TextArea } from "components/designV2/textarea/textarea";
import { ColorV2 } from "config/colors";
import { useReportAbsence } from "hooks/useReportAbsence";
import { AbsenceType } from "model/Schedule";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorModal } from "web-apps/company/components/common/errorModal.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  ActionRow,
  FieldLabel,
  FieldStack,
  ModalBody,
} from "./reportAbsenceModal.styled";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeReportId?: number | null;
  onReportAbsence?: (payload: {
    absenceType: AbsenceType;
    comment: string;
  }) => void;
}

const REPORT_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.NegativeRed,
  hoverBackgroundColor: ColorV2.DeclinedRed,
  activeBackgroundColor: ColorV2.DeclinedRed,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

export const ReportAbsenceModal: React.FC<ModalProps> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const { reportAbsence, isLoading, error } = useReportAbsence();
  const companyId = companyState.company?.id;
  const [absenceType, setAbsenceType] = useState<AbsenceType | "">("");
  const [comment, setComment] = useState("");
  const [isErrorOpen, setIsErrorOpen] = useState(false);

  useEffect(() => {
    if (props.isOpen) return;
    setIsErrorOpen(false);
  }, [props.isOpen]);

  const absenceOptions = useMemo(
    (): Array<{ value: AbsenceType; label: string }> => [
      { value: AbsenceType.SICK_LEAVE, label: t("Schedule.SickLeave") },
      {
        value: AbsenceType.SICK_LEAVE_CHILD,
        label: t("Schedule.SickLeaveChild"),
      },
      { value: AbsenceType.VACATION, label: t("Schedule.Vacation") },
      { value: AbsenceType.PARENTAL_LEAVE, label: t("Schedule.ParentalLeave") },
      {
        value: AbsenceType.LEAVE_OF_ABSENCE,
        label: t("Schedule.LeaveOfAbsence"),
      },
      { value: AbsenceType.OTHER, label: t("Schedule.Other") },
      {
        value: AbsenceType.CANCELLED_BY_EMPLOYER,
        label: t("Schedule.CancelledByEmployer"),
      },
    ],
    [t],
  );

  const handleAbsenceChange = (value?: string | null): void => {
    if (!value) return;
    const selected = absenceOptions.find((option) => option.value === value);
    if (!selected) return;
    setAbsenceType(selected.value);
  };

  const handleClose = (): void => {
    setIsErrorOpen(false);
    props.onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!absenceType || !companyId || !props.timeReportId) return;
    const trimmedComment = comment.trim();
    const result = await reportAbsence({
      companyId,
      timeReportId: props.timeReportId,
      comment: trimmedComment,
      resolution: absenceType,
    });
    if (!result.ok) {
      setIsErrorOpen(true);
      return;
    }
    props.onReportAbsence?.({ absenceType, comment: trimmedComment });
    props.onClose();
  };

  return (
    <Modal
      label={t("CompanyTimeReport.ReportAbsence")}
      isOpen={props.isOpen}
      onClose={handleClose}
      size="small"
      showCloseButton={false}
      contentOverflow="visible"
      footer={
        <ActionRow>
          <SecondaryButton
            label={t("CompanyTimeReport.Cancel")}
            onClick={handleClose}
          />
          <PrimaryButton
            label={t("CompanyTimeReport.ReportAbsence")}
            iconName="v2/absent"
            styleOverrides={REPORT_BUTTON_STYLES}
            disabled={
              !absenceType || !companyId || !props.timeReportId || isLoading
            }
            isLoading={isLoading}
            onClick={handleSubmit}
          />
        </ActionRow>
      }
    >
      <ModalBody>
        <FieldStack>
          <FieldLabel>{t("Schedule.ReasonForAbsence")}</FieldLabel>
          <Dropdown
            options={absenceOptions}
            value={absenceType || undefined}
            onChange={handleAbsenceChange}
            placeholder={t("CompanyTimeReport.ChooseAbsence")}
            fullWidth
          />
        </FieldStack>
        <TextArea
          label={t("Schedule.Comment")}
          value={comment}
          onChange={setComment}
          placeholder={t("Schedule.CommentPlaceholder")}
          maxHeight={240}
        />
      </ModalBody>
      {error && (
        <ErrorModal
          isOpen={isErrorOpen}
          onClose={() => setIsErrorOpen(false)}
          messageKey={error}
        />
      )}
    </Modal>
  );
};
